// Copyright (c) 2019-2023 Cigent Technology Inc. All Rights Reserved.
import { AbstractControl, AsyncValidator, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { IPasswordComponent } from '../../../types/password';
import { Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, first, map, switchMap } from 'rxjs/operators';
import { UserService } from '../../../services/user-service';
import {GroupsService} from '../../../services/groups-service';

export type BooleanValidatorFn = (attrFormControl) => boolean;

export class CustomValidators {

  // Use instead of Validators.email to allow UTF-8 email addresses
  // Note: Standard allows for single character domain names, so a@b is technically a valid email address,
  // here we choose to require at least 4 characters in domain name
  static emailPattern = /^[^@]{1,64}@[^@]{4,253}$/;
  // The original Angular pattern (added line wrap):
  //   EMAIL_REGEXP = /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@
  //   [a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  // See also https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
  // However, we want to allow utf-8, per RFC 6532, which obsoletes RFC 5335, and updates RFC 2045
  // see also RFC 6530 (which obsoletes RFC 5825, 5504, 4952)
  // Note RFC 5322 (updated by 5335, 5336) obsoletes RFC 2822, which obsoletes RFC 822,
  // See also https://tools.ietf.org/id/draft-seantek-mail-regexen-03.html

  static getPasswordValidators(component: IPasswordComponent) {
    return Validators.compose([
       // 1. Password Field is Required
       Validators.required,

       // 2. Make sure password satisfies 3 of 5 categories
       CustomValidators.has3Categories(component),

       // 3. Has a minimum length of 10 characters
       CustomValidators.minByteLength(10),
       // 4. Has a maximum length of 50 characters
       CustomValidators.maxByteLength(50),

       // 5. Has no | symbol
       CustomValidators.hasNoPipeSymbol()
     ]);
  }

  static patternValidator(regex: RegExp, error: ValidationErrors): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      if (!control.value || control.value.length === 0) {
        // if control is empty return no error
        return null;
      }

      // test the value of the control against the regexp supplied
      const valid = regex.test(control.value);
      return valid ? null : error;
    };
  }

  static uuidValidator() {
    return this.patternValidator(/^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i, {invalidUuid: true});
  }

  static duplicateStringValidator(names: string[]): ValidatorFn {
    return (control: AbstractControl) => {
      const alreadyExists = names.find(n => n.toLowerCase() === control.value.toLowerCase());
      return (alreadyExists === undefined) ? null : {duplicateString: true};
    };
  }

  static passwordMatchValidator(controlName: string, matchingControlName: string) {
    return (formGroup: UntypedFormGroup) => {
      const control = formGroup.controls[controlName];
      const matchingControl = formGroup.controls[matchingControlName];

      if (matchingControl.errors && !matchingControl.errors.mustMatch) {
        // return if another validator has already found an error on the matchingControl
        return;
      }

      // set error on matchingControl if validation fails
      if (control.value !== matchingControl.value) {
        matchingControl.setErrors({ mustMatch: true });
      } else {
        matchingControl.setErrors(null);
      }
    };
  }

  static isNumberCheck(): ValidatorFn {
    return (c: AbstractControl): { [key: string]: boolean } | null => {
      const number = /^[.\d]+$/.test(c.value) ? +c.value : NaN;
      if (number !== number) {
        return { 'value': true };
      }
      return null;
    };
  }

  static isPasswordSimilar(currentPassControlName: string, newPassControlName: string) {
    return (formGroup: UntypedFormGroup) => {
      const currentControl = formGroup.controls[currentPassControlName];
      const newControl = formGroup.controls[newPassControlName];

      if (newControl.errors && !newControl.errors.tooSimilar) {
        // return if another validator has already found an error on the matchingControl
        return;
      }
      const currentPassword = '' + currentControl.value;
      const newPassword = '' + newControl.value;
      const currentPassLowercase = currentPassword.toLocaleLowerCase();
      const newPassLowercase = newPassword.toLocaleLowerCase();

      if (currentPassLowercase === newPassLowercase) {
        newControl.setErrors({ tooSimilar: true });
      } else if (CustomValidators.isPasswordReversed(currentPassword, newPassword, 0.66)) {
        newControl.setErrors({ tooSimilar: true });
      } else if (CustomValidators.levenshteinDistanceMatched(currentPassword, newPassword, 0.66)) {
        newControl.setErrors({ tooSimilar: true });
      } else {
        newControl.setErrors(null);
      }
    };
  }

  static isEmailOrSimilarFound(newPassControlName: string, email: string) {
    return (formGroup: UntypedFormGroup) => {
      const newControl = formGroup.controls[newPassControlName];

      if (newControl.errors && !newControl.errors.emailOrSimilar || !email) {
        // return if another validator has already found an error on the matchingControl
        return;
      }
      const newPassword = '' + newControl.value;
      const newPassLowercase = newPassword.toLocaleLowerCase();

      if (newPassLowercase.includes(email) || 
          CustomValidators.levenshteinDistanceMatched(email, newPassLowercase, 0.66)) {
        newControl.setErrors({ emailOrSimilar: true });
      } else {
        newControl.setErrors(null);
      }
    };
  }

  static isPasswordReversed(currentPassword: string, newPassword: string, matchValue: number): boolean {
    const currentPassLowercase = currentPassword.toLocaleLowerCase();
    const newPassLowercase = newPassword.toLocaleLowerCase();

    // Spread syntax is Unicode aware, so we don't use a simple split(), which is not Unicode aware.
    const currentReversed = [...currentPassLowercase].reverse().join('');

    const reversedLevenshteinCheck = CustomValidators.levenshteinDistanceMatched(currentReversed, newPassword, matchValue);
    return currentReversed === newPassLowercase || reversedLevenshteinCheck;
  }

  // Levenshtein distance
  // Iterative with full matrix
  // https://en.wikipedia.org/wiki/Levenshtein_distance
  // param matchValue is any value from [0 - 1]
  static levenshteinDistanceMatched(comparer: string, comparator: string, matchValue: number): boolean {
    let isMatched = false;
    const a = comparer.trim().toLowerCase();
    const b = comparator.trim().toLowerCase();
    const matrix = [];
    let i: number;
    let j: number;
    
    if (a.length === 0) { return false; }
    if (b.length === 0) { return false; }

    // Increment along the first column of each row
    for (i = 0; i <= b.length; i++) {
      matrix[i] = [i];
    }

    // Increment each column in the first row
    for (j = 0; j <= a.length; j++) {
      matrix[0][j] = j;
    }

    // Fill in the rest of the matrix
    // Note: This section uses 1-based strings instead of 0-based strings
    for (i = 1; i <= b.length; i++) {
      for (j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i][j] = matrix[i - 1][j - 1];
        } else {
          matrix[i][j] = Math.min(matrix[i - 1][j - 1] + 1, // substitution
            Math.min(matrix[i][j - 1] + 1, // insertion
              matrix[i - 1][j] + 1)); // deletion
        }
      }
    }

    const levenshteinDistance = matrix[b.length][a.length];
    const cLength = Math.max(a.length, b.length);
    const score = 1.0 - (levenshteinDistance / cLength);
    if (score > matchValue) {
      isMatched = true;
    }
    return isMatched;
  }

  static hasNoPipeSymbol(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (control.errors) {}
      if (control.value?.indexOf('|') > -1) {
        return {containsPipeSymbol: true};
      }
      return null;
    };
  }

  static has3Categories2(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      let count = 0;
      if (!control.value || control.value.length === 0) {
        return null;
      }
      if ( CustomValidators.containsUpperCase(control.value) ) {
        count++;
      }
      if ( CustomValidators.containsLowerCase(control.value) ) {
        count++;
      }
      if ( CustomValidators.containsNumber(control.value) ) {
        count++;
      }
      if ( CustomValidators.containsSpecialChar(control.value) ) {
        count++;
      }
      if ( CustomValidators.containsUnicode(control.value) ) {
        count++;
      }
      return count >= 3 ? null : { complexity: true };
    };
  }

  static has3Categories(component: IPasswordComponent): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } => {
      component.hasLowerCase = false;
      component.hasUpperCase = false;
      component.hasUnicode = false;
      component.hasNumber = false;
      component.hasSpecial = false;

      let count = 0;

      if (!control.value || control.value.length === 0) {
        // if control is empty return no error
        component.categoriesSatisfied = 0;
        return null;
      }

      if ( CustomValidators.containsUpperCase(control.value) ) {
        component.hasUpperCase = true;
        count++;
      }

      if ( CustomValidators.containsLowerCase(control.value) ) {
        component.hasLowerCase = true;
        count++;
      }

      if ( CustomValidators.containsNumber(control.value) ) {
        component.hasNumber = true;
        count++;
      }

      if ( CustomValidators.containsSpecialChar(control.value) ) {
        component.hasSpecial = true;
        count++;
      }

      if ( CustomValidators.containsUnicode(control.value) ) {
         component.hasUnicode = true;
         count++;
      }
      component.categoriesSatisfied = count;

      return count >= 3 ? null : { needsMoreCategories: true };
    };
  }

  static containsUpperCase(value: string): boolean {
      let valid = false;
      for ( let i = 0; i < value.length; i++ ) {
         const char = value.charAt(i);
         if ( char === char.toUpperCase() && char !== char.toLowerCase() ) {
           valid = true;
           break;
         }
      }
      return valid;
  }

  static containsLowerCase(value: string): boolean {
      let valid = false;
      for ( let i = 0; i < value.length; i++ ) {
         const char = value.charAt(i);
         if ( char === char.toLowerCase() && char !== char.toUpperCase() ) {
           valid = true;
           break;
         }
      }
      return valid;
  }

  static containsNumber(value: string): boolean {
    let valid = false;
    if ( /\d/.test(value) ) {
      valid = true;
    }
    return valid;
  }

  static containsSpecialChar(value: string) {
    let valid = false;
    if ( /[!#$*%&()+\-./:;<=>?@\[\\\]^_{|}~'"`]/.test(value) ) {
      valid = true;
    }
    return valid;
  }

  static containsUnicode(value: string) {
    let valid = false;
    // This reg ex just says if NON ascii char is in string
    if ( /[^\x00-\x7F]/.test(value) ) {
      valid = true;
    }
    return valid;
  }

  static conditionalValidator(
    predicate: BooleanValidatorFn,
    errorNamespace?: string): ValidatorFn {
      return formControl => {
        if (!formControl.parent) {
          return null;
        }
        let error = null;
        if (predicate(formControl)) {
          error = {};
          error[errorNamespace] = true;
          formControl.setErrors(error);
        }
        if (errorNamespace && error) {
          const customError = {};
          customError[errorNamespace] = error;
          error = customError;
        }
        return error;
      };
  }

  static minByteLength(minLength: number): ValidatorFn {
    return (control: AbstractControl) => {
      // Get the number of bytes for the password
      const bytesLength = unescape(encodeURIComponent(control.value)).length;
      return bytesLength >= minLength ? null : {minByteLength: {requiredLength: minLength, actualLength: bytesLength}};
    };
  }

  static maxByteLength(maxLength: number): ValidatorFn {
    return (control: AbstractControl) => {
      // Get the number of bytes for the password
      const bytesLength = unescape(encodeURIComponent(control.value)).length;
      return bytesLength <= maxLength ? null : {maxByteLength: {requiredLength: maxLength, actualLength: bytesLength}};
    };
  }

  static uniqueSerialsValidator(): ValidatorFn {
      return (control: AbstractControl): ValidationErrors | null => {
        const value: string = control.value;

        if (!value) {
          return null;
        }

        const serials = value
          .split(/[,\s]/).filter(Boolean);

        const serialFrequency = serials.reduce((acc, serial) => {
          acc[serial] = (acc[serial] || 0) + 1;
          return acc;
        }, {} as Record<string, number>);

        const duplicates = Object.entries(serialFrequency)
          .filter(([, count]) => count > 1)
          .map(([serial]: [string, number]) => serial);

        if (duplicates.length > 0) {
          return { duplicateSerials: { duplicates } };
        }

        return null;
      };
    }

}

export class UniqueGroupNameValidator implements AsyncValidator {
  private initialName: string;

  constructor(private readonly selectedOrgId: string, private readonly groupsService: GroupsService) {}

  validate(control: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    if (control.value === this.initialName) {
      return of(null);
    }
    return control.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      switchMap(v => this.groupsService.getNameExists(v, this.selectedOrgId)),
      map(exists => {
        if(exists) {
          control.setErrors({exists});
          return {exists};
        } else {
          control.setErrors(null);
          return null;
        }
      }),
      first(),
      catchError(() => of(null))
    )
  }

  setInitialName(name: string) {
    this.initialName = name;
  }
}

export class UniqueEmailValidator implements AsyncValidator {

  constructor(private readonly userService: UserService,
              private readonly initialEmail: string | null = null) {}

  validate(ctrl: AbstractControl): Promise<ValidationErrors | null> | Observable<ValidationErrors | null> {
    if (ctrl.value === this.initialEmail) {
      return of(null);
    }
    return ctrl.valueChanges
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap(e => this.userService.checkUserEmailExists(e)),
        map(result => {
          if (result?.exists) {
            ctrl.setErrors({exists: result.exists});
            return {exists: result.exists};
          } else {
            ctrl.setErrors(null);
            return null;
          }
        }),
        first(),
        catchError(error => {
          return of(error);
        })
      )
  }

}
